/**
 * SharedPatientReadOnlyPage — iter570 (Phase 7c)
 *
 * Read-only Expert-mode преглед, който лекарят отваря след като пациентът
 * одобри magic-link invite (`/api/doctor-share/patient/:id/snapshot`).
 *
 * Сигурност:
 *   • Само clinician/admin виждат страницата (Protected route + backend guard)
 *   • Backend връща САМО санитиран snapshot (без password / email / payment)
 *   • Никакви write actions — само "view"
 *
 * UX (BG-first):
 *   • Hero: име + цел + dashboard mode badge
 *   • Body metrics card (тегло / BMI / талия)
 *   • Метаболитен summary (DIL avg + NIS avg за 14д + хранения)
 *   • Активност + общи бройки
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import {
  Loader2, ArrowLeft, ShieldCheck, Scale, Ruler, Activity,
  Calendar, Flame, FlaskConical, AlertCircle, Heart,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, unit, hint, isDark, testId, color = "violet" }) {
  const accent = {
    violet:  isDark ? "text-violet-300"  : "text-violet-700",
    sky:     isDark ? "text-sky-300"     : "text-sky-700",
    emerald: isDark ? "text-emerald-300" : "text-emerald-700",
    amber:   isDark ? "text-amber-300"   : "text-amber-700",
    rose:    isDark ? "text-rose-300"    : "text-rose-700",
  }[color] || (isDark ? "text-violet-300" : "text-violet-700");
  return (
    <div
      className={`rounded-2xl border p-4 ${isDark ? "bg-[hsl(222,47%,9%)] border-white/5" : "bg-white border-gray-200"}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`w-4 h-4 ${accent}`} />
        <span className={`text-[11px] uppercase tracking-wider font-bold ${isDark ? "text-gray-300" : "text-gray-700"}`}>
          {label}
        </span>
      </div>
      <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
        {value == null || value === "" ? "—" : value}
        {value != null && unit && (
          <span className={`ml-1 text-xs font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>{unit}</span>
        )}
      </div>
      {hint && (
        <div className={`text-[10px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>{hint}</div>
      )}
    </div>
  );
}

const DOCTOR_ROLES = ["admin", "clinician"];

export default function SharedPatientReadOnlyPage() {
  const { patientId } = useParams();
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const bg = language === "bg";

  const [snap, setSnap] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Access guard
  useEffect(() => {
    if (!user) return;
    if (!DOCTOR_ROLES.includes((user.role || "").toLowerCase())) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token");
        const { data } = await axios.get(`${API}/doctor-share/patient/${patientId}/snapshot`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (mounted) setSnap(data);
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || (bg ? "Достъпът е отказан или линкът е невалиден." : "Access denied or invalid share."));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [API, patientId, bg]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(bg ? "bg-BG" : "en-US", { day: "numeric", month: "short", year: "numeric" });
    } catch { return "—"; }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? "bg-[hsl(222,47%,6%)]" : "bg-[#F8FAFC]"}`}>
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
      </div>
    );
  }

  if (error || !snap) {
    return (
      <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-gray-900"}`}>
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
          <button
            onClick={() => navigate("/doctor")}
            data-testid="shared-back-btn"
            className={`inline-flex items-center gap-1 mb-4 text-xs font-semibold ${isDark ? "text-violet-300" : "text-violet-700"}`}
          >
            <ArrowLeft className="w-4 h-4" />
            {bg ? "Назад към портала" : "Back to portal"}
          </button>
          <div className={`rounded-2xl border p-6 ${isDark ? "bg-rose-500/10 border-rose-500/30" : "bg-rose-50 border-rose-200"}`}
               data-testid="shared-patient-error">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className={`w-5 h-5 ${isDark ? "text-rose-300" : "text-rose-600"}`} />
              <h1 className={`text-base font-bold ${isDark ? "text-white" : "text-rose-900"}`}>
                {bg ? "Достъпът е невъзможен" : "Access unavailable"}
              </h1>
            </div>
            <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              {error}
            </p>
          </div>
        </main>
      </div>
    );
  }

  const mode = snap.dashboard_mode || "easy";
  const perinatal = snap.perinatal || {};
  const perinatalStatus = perinatal.status || perinatal.mode;

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-gray-900"}`}
         data-testid="shared-patient-readonly-page">
      <SEO
        title={bg ? "Споделен пациент — Nutramilo" : "Shared patient — Nutramilo"}
        description={bg ? "Read-only клиничен преглед на споделен пациент." : "Read-only clinical view of shared patient."}
        path={`/doctor/shared/${patientId}`}
      />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-5 sm:py-7">
        <button
          onClick={() => navigate("/doctor")}
          data-testid="shared-back-btn"
          className={`inline-flex items-center gap-1 mb-4 text-xs font-semibold ${isDark ? "text-violet-300 hover:text-violet-200" : "text-violet-700 hover:text-violet-900"}`}
        >
          <ArrowLeft className="w-4 h-4" />
          {bg ? "Назад към портала" : "Back to portal"}
        </button>

        {/* Hero */}
        <section
          className={`rounded-2xl p-5 sm:p-6 mb-5 border ${
            isDark
              ? "bg-gradient-to-br from-violet-900/30 to-fuchsia-900/20 border-violet-700/30"
              : "bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-200"
          }`}
          data-testid="shared-patient-hero"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                {bg ? "Read-only клиничен преглед" : "Read-only clinical view"}
              </div>
              <h1 className={`text-xl sm:text-2xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {snap.patient_name}
              </h1>
              {snap.personal_goal && (
                <p className={`mt-1.5 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  <Heart className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                  {bg ? "Цел:" : "Goal:"} <strong>{snap.personal_goal}</strong>
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full ${
                isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"
              }`}>
                <ShieldCheck className="w-3 h-3" />
                {bg ? "Одобрено от пациента" : "Patient-approved"}
              </span>
              <span className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {bg ? "от" : "since"} {fmtDate(snap.share_approved_at)}
              </span>
              <span className={`mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                isDark ? "bg-white/10 text-gray-300" : "bg-white text-gray-600 border border-gray-200"
              }`}>
                {bg ? "Режим:" : "Mode:"} <strong className="uppercase">{mode}</strong>
              </span>
            </div>
          </div>
          {perinatalStatus && perinatalStatus !== "none" && (
            <div className={`mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${
              isDark ? "bg-pink-500/15 text-pink-200" : "bg-pink-50 text-pink-700"
            }`} data-testid="shared-patient-perinatal-badge">
              <Activity className="w-3 h-3" />
              {bg ? `Перинатален статус: ${perinatalStatus}` : `Perinatal status: ${perinatalStatus}`}
            </div>
          )}
        </section>

        {/* Body metrics */}
        <h2 className={`text-xs uppercase tracking-wider font-bold mb-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {bg ? "Телесни данни" : "Body metrics"}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Scale} label={bg ? "Тегло" : "Weight"} value={snap.body?.weight_kg} unit="kg" isDark={isDark} testId="shared-stat-weight" />
          <StatCard icon={Ruler} label={bg ? "Височина" : "Height"} value={snap.body?.height_cm} unit="cm" isDark={isDark} testId="shared-stat-height" color="sky" />
          <StatCard icon={Activity} label={bg ? "BMI" : "BMI"} value={snap.body?.bmi} isDark={isDark} testId="shared-stat-bmi" color="emerald"
                    hint={bg ? "тегло / ръст²" : "weight / height²"} />
          <StatCard icon={Ruler} label={bg ? "Талия" : "Waist"} value={snap.body?.waist_cm} unit="cm" isDark={isDark} testId="shared-stat-waist" color="amber" />
        </div>

        {/* Metabolic 14d */}
        <h2 className={`text-xs uppercase tracking-wider font-bold mb-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {bg ? "Метаболитен преглед (14 дни)" : "Metabolic overview (14d)"}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <StatCard
            icon={Flame}
            label={bg ? "DIL средно" : "DIL avg"}
            value={snap.metabolic_14d?.dil_avg}
            isDark={isDark}
            color="rose"
            testId="shared-stat-dil-avg"
            hint={bg ? "точки/ястие" : "pts/meal"}
          />
          <StatCard
            icon={Activity}
            label={bg ? "NIS средно" : "NIS avg"}
            value={snap.metabolic_14d?.nis_avg_pct}
            unit="%"
            isDark={isDark}
            color="violet"
            testId="shared-stat-nis-avg"
          />
          <StatCard
            icon={Calendar}
            label={bg ? "Записани хранения" : "Meals logged"}
            value={snap.metabolic_14d?.meals_logged}
            isDark={isDark}
            color="emerald"
            testId="shared-stat-meals"
            hint={bg ? "последни 14 дни" : "last 14 days"}
          />
        </div>

        {/* Totals */}
        <h2 className={`text-xs uppercase tracking-wider font-bold mb-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {bg ? "Общи данни" : "Totals"}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <StatCard
            icon={Calendar}
            label={bg ? "HI снимки" : "HI snapshots"}
            value={snap.totals?.hi_snapshots}
            isDark={isDark}
            testId="shared-stat-hi-total"
            hint={bg ? "всички записи" : "all records"}
          />
          <StatCard
            icon={FlaskConical}
            label={bg ? "Лабораторни панели" : "Lab panels"}
            value={snap.totals?.lab_panels}
            isDark={isDark}
            color="sky"
            testId="shared-stat-labs-total"
          />
          <StatCard
            icon={Calendar}
            label={bg ? "Последна активност" : "Last activity"}
            value={fmtDate(snap.last_activity?.last_meal_at || snap.last_activity?.last_hi_snapshot_at)}
            isDark={isDark}
            color="amber"
            testId="shared-stat-last-activity"
          />
        </div>

        {/* Footer notice */}
        <div className={`mt-2 rounded-xl p-3 text-[11px] leading-relaxed border ${
          isDark ? "bg-white/[0.02] border-white/5 text-gray-400" : "bg-gray-50 border-gray-200 text-gray-600"
        }`} data-testid="shared-patient-footer-notice">
          <ShieldCheck className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
          {bg
            ? "Този преглед е read-only. Пациентът може да оттегли достъпа по всяко време от своя профил. Не съхраняваме лична преписка, плащания или пароли."
            : "This view is read-only. The patient can revoke access at any time from their profile. We do not expose personal messages, payments or passwords."}
          <div className="mt-1">
            <Link
              to="/doctor"
              className={`underline ${isDark ? "text-violet-300" : "text-violet-700"}`}
              data-testid="shared-patient-back-link"
            >
              {bg ? "Към доктор портала" : "To doctor portal"}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
